/* Roster Transmission slideshow — every league poster (key players, schematics,
   and the official season posters 2313→2321) in one TALL vertical frame.
   The deck is shuffled on every visit and auto-advance jumps to a RANDOM slide,
   so the transmission never plays the same twice. Pauses on hover/touch. */
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { artAssets } from "../data/site";

const SLIDES = [
  {
    id: "champion-trophy",
    src: artAssets.riotballChampionTrophy,
    label: "DOLO — THE CHAMPION YEARS",
    sub: "RED FORCE // CHAMPIONSHIP TITLE HAUL",
    alt: "Dolo in his Red Force prime hoisting the golden AL Riotball championship trophy",
  },
  {
    id: "red-force-victory",
    src: artAssets.riotballRedForceVictory,
    label: "TEAM BATTLE — RED FORCE VICTORY",
    sub: "UNITS 07 · 04 · 08 · 02 · 09 // CHAMPIONS",
    alt: "Red Force units 07, 04, 08, 02 and 09 hoisting the AL Riotball championship trophy",
  },
  {
    id: "poster-enforcer",
    src: artAssets.riotballPosterEnforcer,
    label: "UNIT 07 — THE ENFORCER",
    sub: "CORE POSSESSION // ARENA: SECTOR 9 ZONES",
    alt: "AL Riotball poster — Red Force athlete Unit 07 The Enforcer holding the glowing Core",
  },
  {
    id: "enforcer-schematic",
    src: artAssets.riotballEnforcerSchematic,
    label: "UNIT 07 — THE ENFORCER",
    sub: "CHASSIS SCHEMATIC // SERVO-ACTUATOR PISTONS",
    alt: "AL Riotball technical sheet — The Enforcer Unit 07 chassis schematic",
  },
  {
    id: "red-force-elite",
    src: artAssets.riotballRedForceElite,
    label: "TEAM RED FORCE — KEY PLAYERS",
    sub: "RIOTBALL ELITE // SCYTHE - WALL - OVERMIND",
    alt: "AL Riotball Elite poster — Red Force key players The Scythe, The Wall, and The Overmind",
  },
  {
    id: "roster-2355",
    src: artAssets.riotballRoster2355,
    label: "CHAMPIONSHIP LEAGUE — KEY PLAYER ROSTER",
    sub: "RED FORCE VS WHITE GUARDS // 2355 SEASON",
    alt: "AL Riotball Championship League key player roster — Unit 11 The Striker, Unit 07 The Enforcer, Unit 04 The Defender",
  },
  {
    id: "season-2313",
    src: "/assets/riotball-season-2313.jpg",
    label: "AL RIOTBALL PRO SEASON 2313",
    sub: "AL RIOTBALL CHAMPIONSHIP PLAYOFFS",
    alt: "Official 2313 season poster — the AL Riotball core floating over a neon playoff street",
  },
  {
    id: "season-2315",
    src: "/assets/riotball-season-2315.jpg",
    label: "AL RIOTBALL PRO SEASON 2315",
    sub: "CHAMPIONSHIP PLAYOFFS // WALL PROCLAMATION",
    alt: "Official 2315 season poster — the charged AL Riotball under the playoff proclamation wall",
  },
  {
    id: "season-2319",
    src: "/assets/riotball-season-2319.jpg",
    label: "AL RIOTBALL PRO SEASON 2319",
    sub: "OFFICIAL SEASON POSTER // CORE CARRIER",
    alt: "Official 2319 season poster — an armored carrier gripping the sparking Core mid-drive",
  },
  {
    id: "season-2321-red",
    src: "/assets/riotball-season-2321-red.jpg",
    label: "AL RIOTBALL PRO SEASON 2321",
    sub: "GRAND FINALS // NEO-KYOTO DOME",
    alt: "Official 2321 season poster — armored rivals clash over the burning Core at Neo-Kyoto Dome",
  },
  {
    id: "season-2321-green",
    src: "/assets/riotball-season-2321-green.jpg",
    label: "AL RIOTBALL PRO SEASON 2321",
    sub: "OFFICIAL POSTER // TOXIC CIRCUIT",
    alt: "Official 2321 alternate season poster — the green-cored champion cradles the AL Riotball",
  },
];

const ROTATE_MS = 4600;

const shuffle = (arr) => {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
};

export function RosterSlideshow() {
  /* the deck order is randomized per visit, and auto-advance jumps to a random
     slide rather than the next one — the whole transmission is shuffled */
  const [deck] = useState(() => shuffle(SLIDES));
  const [index, setIndex] = useState(0);
  const pausedRef = useRef(false);
  const slide = deck[index];

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const timer = window.setInterval(() => {
      if (pausedRef.current) return;
      setIndex((i) => {
        let next = i;
        while (next === i) next = Math.floor(Math.random() * deck.length);
        return next;
      });
    }, ROTATE_MS);
    return () => window.clearInterval(timer);
  }, [deck.length]);

  const go = (next) => setIndex(((next % deck.length) + deck.length) % deck.length);

  return (
    <div
      className="roster-slider"
      data-testid="roster-slideshow"
      onMouseEnter={() => { pausedRef.current = true; }}
      onMouseLeave={() => { pausedRef.current = false; }}
      onTouchStart={() => { pausedRef.current = true; }}
      onTouchEnd={() => { window.setTimeout(() => { pausedRef.current = false; }, 2500); }}
    >
      <div className="roster-slider-frame">
        <AnimatePresence mode="wait">
          <motion.figure
            key={slide.id}
            className="roster-slide"
            data-testid={`roster-slide-${slide.id}`}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          >
            <img src={slide.src} alt={slide.alt} loading="lazy" />
            <figcaption>
              <strong data-testid="roster-slide-label">{slide.label}</strong>
              <span>{slide.sub}</span>
            </figcaption>
          </motion.figure>
        </AnimatePresence>

        <button type="button" className="playoff-arrow is-prev" onClick={() => go(index - 1)} data-testid="roster-slideshow-prev" aria-label="Previous poster">
          <ChevronLeft size={20} />
        </button>
        <button type="button" className="playoff-arrow is-next" onClick={() => go(index + 1)} data-testid="roster-slideshow-next" aria-label="Next poster">
          <ChevronRight size={20} />
        </button>
      </div>

      <div className="playoff-dots" data-testid="roster-slideshow-dots">
        {deck.map((s, i) => (
          <button
            type="button"
            key={s.id}
            className={`playoff-dot${i === index ? " is-active" : ""}`}
            onClick={() => go(i)}
            data-testid={`roster-slideshow-dot-${i}`}
            aria-label={`Show poster ${i + 1}: ${s.label}`}
          />
        ))}
      </div>
    </div>
  );
}
